import React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

type KoobPopoverProps = {
  /** Rendered asChild — must forward a ref (KoobButton does). */
  trigger: React.ReactNode;
  align?: PopoverPrimitive.PopoverContentProps['align'];
  side?: PopoverPrimitive.PopoverContentProps['side'];
  /** Controlled open state (optional — uncontrolled when omitted). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** No padding and no scroll of its own: the content lays out its own
   * header, scrolling body and footer. */
  flush?: boolean;
  children: React.ReactNode;
};

// A popover's content can open a real modal (react-modal / headlessui);
// while one is up, outside interactions and Escape belong to the modal and
// must not dismiss (unmount) the popover under it.
const hasOpenModal = () =>
  typeof document !== 'undefined' &&
  !!document.querySelector('.ReactModal__Overlay, [id^="headlessui-dialog"]');

/**
 * Small padded popover card for inline filter/editor content (steppers,
 * inputs, forms) — the free-form sibling of KoobMenu, which is for item
 * lists that close on click.
 */
const KoobPopover = ({
  trigger,
  align = 'start',
  side = 'bottom',
  open,
  onOpenChange,
  flush = false,
  children,
}: KoobPopoverProps) => (
  <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        side={side}
        sideOffset={6}
        collisionPadding={8}
        onInteractOutside={(event) => {
          if (hasOpenModal()) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (hasOpenModal()) event.preventDefault();
        }}
        className={`z-popover max-h-[min(80vh,var(--radix-popover-content-available-height))] min-w-64 rounded-lg border border-solid border-border bg-white shadow-md ${
          flush ? 'flex flex-col overflow-hidden' : 'overflow-y-auto p-4'
        }`}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>
);

export default KoobPopover;
